import { Divider, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiDropzone from 'features/ui/base-components/pai-dropzone';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  listName: string;
  isUploading: boolean;
  previewRows: any[];

  setListName(listName: string): void;

  onFileDropHandler(files: File[]): void;

  handleStartEnrichment(): void;
}

const LeadListUploadCreation: React.FC<Props> = ({ listName, isUploading, previewRows, setListName, onFileDropHandler, handleStartEnrichment }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <>
      <div>
        <Typography className={classes.subTitle} mb={1}>
          {t('uploadOwnLeadList')}
        </Typography>
        <Typography className={classes.description}>{t('uploadOwnLeadListDescription')}</Typography>
      </div>
      <PaiTextField
        label={t('listName')}
        placeholder={t('listNamePlaceholder')}
        variant={'background'}
        value={listName}
        onChange={(e) => setListName(e.target.value)}
      />
      <PaiDropzone maxWidth="100%" height="200px" onDrop={onFileDropHandler} />
      <Divider sx={{ margin: '8px 0' }} />
      <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'} gap={2}>
        <PaiButton
          text={t('startEnrichment')}
          border={'rounded'}
          action={handleStartEnrichment}
          disabled={!listName.trim() || previewRows.length === 0}
          loading={isUploading}
        />
      </Stack>
      {previewRows.length > 0 && (
        <>
          <Typography className={classes.subTitle}>{t('uploadSuccessPreview')}</Typography>
          <section className={classes.previewSection}>
            <TableContainer className={classes.tableContainer}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('firstName')}</TableCell>
                    <TableCell>{t('lastName')}</TableCell>
                    <TableCell>{t('email')}</TableCell>
                    <TableCell>{t('phoneNumber')}</TableCell>
                    <TableCell>{t('position')}</TableCell>
                    <TableCell>{t('company')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.slice(0, 10).map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.contact.firstName}</TableCell>
                      <TableCell>{item.contact.lastName}</TableCell>
                      <TableCell>{item.contact.email}</TableCell>
                      <TableCell>{item.contact.phone}</TableCell>
                      <TableCell>{item.contact.role}</TableCell>
                      <TableCell>{item.company.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </section>
        </>
      )}
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  subTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
  },
  description: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.text.secondary,
  },
  previewSection: {
    overflow: 'auto',
  },
  tableContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: '8px',
    '& th': {
      border: 'none',
      padding: '4px 16px',
      fontSize: theme.typography.pxToRem(13),
      backgroundColor: theme.palette.background.paper,
      whiteSpace: 'nowrap',
      fontWeight: 'bold',
    },
    '& td, th': {
      border: 'none',
      borderBottom: '1px solid #ddd',
      padding: '8px 16px',
      textAlign: 'left',
      verticalAlign: 'middle',
      fontSize: theme.typography.pxToRem(13),
    },
    '& tr:last-child td': {
      borderBottom: 'none',
    },
  },
}));

export default LeadListUploadCreation;
